/**
 * PDF/print HTML builders — extracted from AppShell.jsx for reuse and testing.
 * Each builder returns a complete <!DOCTYPE html> document string.
 */

const POWERED_FOOTER =
  '<span style="font-weight:700;letter-spacing:0.04em;color:#e11d48">Powered by CrimsonScan</span> · crimsonscan.com · AI-Driven Materials Discovery';

// ---------------------------------------------------------------------------
// Economic Report
// ---------------------------------------------------------------------------

function buildEconomicReportHTML(data) {
  const generated = new Date(data.generated_at).toUTCString();
  const em = data.economic_metrics;
  const row = (label, val) => val != null && val !== '' ? `<tr><td class="lbl">${label}</td><td>${val}</td></tr>` : '';
  const section = (title) => `<div class="sec">${title}</div>`;

  const metricsTable = em ? `
    ${section('Economic Metrics')}
    <table><tbody>
      ${row('Estimated BOM Cost', `<b>${em.cost_low} – ${em.cost_high} / kg</b>`)}
      ${row('Energy Intensity', `${em.energy_label} (${em.energy_range})`)}
      ${row('Cost Volatility', em.volatility_level + (em.volatility_dominant ? ` — ${em.volatility_dominant} dominant` : ''))}
      ${row('Recyclability', `${em.recyclability_level} (score ${em.recyclability_score})`)}
      ${row('Supply Concentration', `${em.supply_concentration_index} — ${em.supply_concentration_level} risk`)}
    </tbody></table>
    ${em.element_details && Object.keys(em.element_details).length > 0 ? `
      <p style="font-size:9pt;font-weight:700;margin:10pt 0 4pt">Per-element price breakdown</p>
      <table><thead><tr><th>Element</th><th>Price / kg</th><th>Mass frac.</th><th>Source</th><th>Date</th></tr></thead><tbody>
        ${Object.entries(em.element_details).map(([el, d]) =>
          `<tr><td><b>${el}</b></td><td style="font-family:monospace">${d.price_label ?? 'N/A'}</td><td style="font-family:monospace">${d.mass_fraction != null ? (d.mass_fraction * 100).toFixed(1) + '%' : '—'}</td><td>${d.source ?? '—'}</td><td>${d.date ?? '—'}</td></tr>`
        ).join('')}
      </tbody></table>
    ` : ''}
    ${em.data_sources?.length ? `<p style="font-size:8pt;color:#888;margin-top:6pt">Sources: ${em.data_sources.join(' · ')}</p>` : ''}
  ` : '';

  const execSummary = data.executive_summary
    ? `${section('Executive Summary')}<div class="body">${data.executive_summary}</div>` : '';

  const elemental = data.elemental_economics ? `
    ${section('Elemental Economics')}
    ${data.elemental_economics.assessment ? `<div class="body">${data.elemental_economics.assessment}</div>` : ''}
    ${data.elemental_economics.advantages?.length ? `<p style="margin:6pt 0 3pt;font-size:9pt;font-weight:700">Advantages</p><ul>${data.elemental_economics.advantages.map(a => `<li>${a}</li>`).join('')}</ul>` : ''}
    ${data.elemental_economics.concerns?.length ? `<p style="margin:6pt 0 3pt;font-size:9pt;font-weight:700">Concerns</p><ul>${data.elemental_economics.concerns.map(c => `<li>${c}</li>`).join('')}</ul>` : ''}
  ` : '';

  const synthesis = data.synthesis_viability ? `
    ${section('Synthesis Viability')}
    ${data.synthesis_viability.complexity ? `<p style="margin:0 0 6pt;font-size:10pt">Complexity: <b>${data.synthesis_viability.complexity}</b></p>` : ''}
    ${data.synthesis_viability.assessment ? `<div class="body">${data.synthesis_viability.assessment}</div>` : ''}
  ` : '';

  const market = data.market_potential ? `
    ${section('Market Potential')}
    ${data.market_potential.applications?.length ? `<p style="margin:0 0 6pt;font-size:9pt">${data.market_potential.applications.join(' · ')}</p>` : ''}
    ${data.market_potential.assessment ? `<div class="body">${data.market_potential.assessment}</div>` : ''}
  ` : '';

  const risk = data.risk_assessment ? `
    ${section('Risk Assessment')}
    ${data.risk_assessment.overall ? `<p style="margin:0 0 6pt;font-size:10pt">Overall: <b>${data.risk_assessment.overall} risk</b></p>` : ''}
    ${data.risk_assessment.factors?.length ? `<ul>${data.risk_assessment.factors.map(f => `<li>${f}</li>`).join('')}</ul>` : ''}
  ` : '';

  return `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>Economic Report</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:Georgia,'Times New Roman',serif;font-size:11pt;color:#111;background:#fff;padding:0.75in 1in}
  h1{font-size:14pt;text-align:center;margin:0 0 4pt;line-height:1.4}
  .meta{font-size:9pt;text-align:center;color:#555;margin-bottom:24pt}
  .sec{font-size:10pt;font-weight:700;text-transform:uppercase;letter-spacing:.06em;margin:18pt 0 6pt;border-bottom:0.5pt solid #ccc;padding-bottom:3pt}
  .body{font-size:10.5pt;line-height:1.75;color:#222;margin-bottom:10pt}
  table{width:100%;border-collapse:collapse;margin:8pt 0;font-size:9pt}
  th{background:#f2f2f2;padding:4pt 8pt;text-align:left;border:0.5pt solid #bbb;font-weight:700}
  td{padding:4pt 8pt;border:0.5pt solid #bbb}td.lbl{color:#555;white-space:nowrap;width:35%}
  ul{padding-left:18pt;margin:4pt 0}li{margin-bottom:4pt;font-size:10.5pt;line-height:1.6}
  .footer{margin-top:28pt;padding-top:6pt;border-top:0.5pt solid #ccc;font-size:8pt;color:#888;text-align:center}
  @media print{body{padding:0}}
</style></head><body>
<h1>Economic Report</h1>
<div class="meta">CrimsonScan · Generated ${generated}</div>
${metricsTable}${execSummary}${elemental}${synthesis}${market}${risk}
<div class="footer">Economic report generated ${generated} · Estimates based on live commodity data. Not investment advice.</div>
<div class="footer" style="margin-top:6pt;border:none;padding-top:0">${POWERED_FOOTER}</div>
</body></html>`;
}

// ---------------------------------------------------------------------------
// Scientific Document
// ---------------------------------------------------------------------------

function buildScientificDocHTML(data) {
  const generated = new Date(data.generated_at).toUTCString();
  const { summary: sm, structural_properties: sp, integrated_analyses: ia } = data;
  const row = (label, val) => val != null && val !== '' ? `<tr><td class="lbl">${label}</td><td>${val}</td></tr>` : '';
  const section = (n, title) => `<div class="sec">${n}. ${title}</div>`;

  const abstract = (() => {
    const crystalSystem = ia?.symmetry?.crystal_system || 'crystalline';
    const parts = [
      `We report the computational discovery of ${sm.formula}, a ${crystalSystem} inorganic compound generated via generative diffusion modelling (MatterGen).`,
    ];
    let structPart = `The unit cell comprises ${sm.atom_count} atom${sm.atom_count !== 1 ? 's' : ''}`;
    if (sp.density != null) structPart += ` with an estimated density of ${sp.density} g cm⁻³`;
    if (sp.packing_fraction != null) structPart += ` and a packing fraction of ${(sp.packing_fraction * 100).toFixed(2)}%`;
    parts.push(structPart + '.');
    if (ia?.symmetry) parts.push(`Symmetry analysis identifies the space group as ${ia.symmetry.space_group} (No. ${ia.symmetry.space_group_number}), belonging to the ${ia.symmetry.crystal_system} crystal system.`);
    if (ia?.dft_surrogate) {
      const d = ia.dft_surrogate;
      let dftPart = `CHGNet DFT surrogate predicts a formation energy of ${d.formation_energy_per_atom} eV/atom`;
      if (d.stability_label) dftPart += ` (${d.stability_label})`;
      if (d.magnetic_character && d.magnetic_character !== 'non-magnetic') dftPart += ` with ${d.magnetic_character} character`;
      parts.push(dftPart + '.');
    }
    if (sm.score_pct != null) {
      parts.push(`The structure achieved an absolute structural quality score of ${sm.score_pct}% (${sm.quality_class}), ranking #${sm.rank} in the current set.`);
    }
    return parts.join(' ');
  })();

  const symSection = ia?.symmetry ? `
    ${section(2, 'Crystal Structure')}
    <table><tbody>
      ${row('Space group', `<b>${ia.symmetry.space_group}</b> (No. ${ia.symmetry.space_group_number})`)}
      ${row('Crystal system', ia.symmetry.crystal_system)}
      ${row('Point group', ia.symmetry.point_group)}
      ${row('Hall symbol', ia.symmetry.hall_symbol)}
      ${row('Symmetry operations', ia.symmetry.n_symmetry_ops)}
    </tbody></table>
    ${ia.symmetry.lattice ? `
      <p style="font-size:9pt;font-weight:700;margin:10pt 0 4pt">2a. Lattice Parameters</p>
      <table><thead><tr><th>a (Å)</th><th>b (Å)</th><th>c (Å)</th><th>α (°)</th><th>β (°)</th><th>γ (°)</th><th>V (Å³)</th></tr></thead><tbody>
        <tr>${[ia.symmetry.lattice.a, ia.symmetry.lattice.b, ia.symmetry.lattice.c, ia.symmetry.lattice.alpha, ia.symmetry.lattice.beta, ia.symmetry.lattice.gamma, ia.symmetry.lattice.volume].map(v => `<td style="font-family:monospace">${v ?? '—'}</td>`).join('')}</tr>
      </tbody></table>
    ` : ''}
    ${ia.symmetry.wyckoff_sites?.length ? `
      <p style="font-size:9pt;font-weight:700;margin:10pt 0 4pt">2b. Wyckoff Positions</p>
      <table><thead><tr><th>Element</th><th>Site</th><th>Mult.</th><th>Fractional coordinates</th></tr></thead><tbody>
        ${ia.symmetry.wyckoff_sites.map(w => `<tr><td><b>${w.element}</b></td><td>${w.wyckoff_letter}</td><td>${w.multiplicity}</td><td style="font-family:monospace">${(w.frac_coords || []).map(c => c.toFixed(4)).join(', ')}</td></tr>`).join('')}
      </tbody></table>
    ` : ''}
  ` : `${section(2, 'Crystal Structure')}<p style="font-style:italic;color:#888">Symmetry data not available — run Symmetry analysis to populate this section.</p>`;

  const bondSection = ia?.bond_stats ? `
    ${section(3, 'Bonding Environment')}
    <table><tbody>
      ${row('Total bonds', ia.bond_stats.count)}
      ${row('Bond length range', `${ia.bond_stats.min} – ${ia.bond_stats.max} Å`)}
      ${row('Mean bond length', `${ia.bond_stats.mean} Å`)}
      ${row('Std. deviation', `${ia.bond_stats.std} Å`)}
    </tbody></table>
    ${ia.bond_pairs?.length ? `
      <p style="font-size:9pt;font-weight:700;margin:10pt 0 4pt">3a. Per-Pair Bond Statistics</p>
      <table><thead><tr><th>Pair</th><th>Count</th><th>Min (Å)</th><th>Max (Å)</th><th>Mean (Å)</th></tr></thead><tbody>
        ${ia.bond_pairs.map(bp => `<tr><td><b>${bp.pair}</b></td><td>${bp.count}</td><td style="font-family:monospace">${bp.min}</td><td style="font-family:monospace">${bp.max}</td><td style="font-family:monospace">${bp.mean}</td></tr>`).join('')}
      </tbody></table>
    ` : ''}
  ` : `${section(3, 'Bonding Environment')}<p style="font-style:italic;color:#888">Bond distribution data not available.</p>`;

  const coordSection = ia?.voronoi_summary?.length ? `
    ${section(4, 'Coordination Environment')}
    <table><thead><tr><th>#</th><th>Element</th><th>CN</th><th>Neighbor environment</th></tr></thead><tbody>
      ${ia.voronoi_summary.map(c => `<tr><td>${c.index}</td><td><b>${c.element}</b></td><td>${c.coordination_number}</td><td>${Object.entries(c.neighbor_counts || {}).map(([el, n]) => `${el}×${n}`).join(', ')}</td></tr>`).join('')}
    </tbody></table>
  ` : `${section(4, 'Coordination Environment')}<p style="font-style:italic;color:#888">Voronoi data not available.</p>`;

  return `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>${data.title || sm.formula} — Scientific Document</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:Georgia,'Times New Roman',serif;font-size:11pt;color:#111;background:#fff;padding:0.75in 1in}
  h1{font-size:14pt;text-align:center;margin:0 0 4pt;line-height:1.4}
  .meta{font-size:9pt;text-align:center;color:#555;margin-bottom:16pt}
  .abstract{border-left:2pt solid #444;padding:8pt 12pt;font-style:italic;font-size:10pt;color:#333;margin-bottom:16pt}
  .sec{font-size:10pt;font-weight:700;text-transform:uppercase;letter-spacing:.06em;margin:18pt 0 6pt;border-bottom:0.5pt solid #ccc;padding-bottom:3pt}
  .body{font-size:10.5pt;line-height:1.75;color:#222;margin-bottom:10pt}
  table{width:100%;border-collapse:collapse;margin:8pt 0;font-size:9pt}
  th{background:#f2f2f2;padding:4pt 8pt;text-align:left;border:0.5pt solid #bbb;font-weight:700}
  td{padding:4pt 8pt;border:0.5pt solid #bbb}td.lbl{color:#555;white-space:nowrap;width:35%}
  .footer{margin-top:28pt;padding-top:6pt;border-top:0.5pt solid #ccc;font-size:8pt;color:#888;text-align:center}
  @media print{body{padding:0}}
</style></head><body>
<h1>${data.title || sm.formula}</h1>
<div class="meta">CrimsonScan · Computational Materials Discovery Report · Generated ${generated}</div>
<div class="sec">Abstract</div>
<div class="abstract">${abstract}</div>
${section(1, 'Compound Identification')}
<table><tbody>
  ${row('Empirical formula', `<b>${sm.formula}</b>`)}
  ${row('Atom count', sm.atom_count)}
  ${sm.quality_class ? row('Quality classification', `<b>${sm.quality_class}</b> (score ${sm.score_pct}%, rank #${sm.rank})`) : ''}
  ${row('Density (est.)', sp.density != null ? `${sp.density} g cm⁻³` : null)}
  ${row('Packing fraction', sp.packing_fraction != null ? `${(sp.packing_fraction * 100).toFixed(2)} %` : null)}
  ${row('Min. interatomic dist.', sp.min_interatomic_distance != null ? `${sp.min_interatomic_distance} Å` : null)}
</tbody></table>
${symSection}${bondSection}${coordSection}
${ia?.dft_surrogate ? `
    ${section(5, 'DFT Surrogate (CHGNet)')}
    <table><tbody>
      ${row('Stability', `<b>${ia.dft_surrogate.stability_label}</b>`)}
      ${row('Formation energy', ia.dft_surrogate.formation_energy_per_atom != null ? `${ia.dft_surrogate.formation_energy_per_atom} eV/atom` : null)}
      ${row('Total energy', ia.dft_surrogate.energy_per_atom != null ? `${ia.dft_surrogate.energy_per_atom} eV/atom` : null)}
      ${row('Max force', ia.dft_surrogate.forces_max_eV_A != null ? `${ia.dft_surrogate.forces_max_eV_A} eV/Å` : null)}
      ${row('Max stress', ia.dft_surrogate.stress_GPa != null ? `${ia.dft_surrogate.stress_GPa} GPa` : null)}
      ${row('Magnetic character', ia.dft_surrogate.magnetic_character || null)}
      ${row('Avg |magmom|', ia.dft_surrogate.magmom_per_atom != null ? `${ia.dft_surrogate.magmom_per_atom} μB` : null)}
    </tbody></table>
    <p style="font-size:8pt;color:#888;margin-top:4pt">CHGNet universal neural-network potential. Formation energy relative to DFT elemental references (Materials Project, PBE).</p>
  ` : `${section(5, 'DFT Surrogate (CHGNet)')}<p style="font-style:italic;color:#888">DFT surrogate data not available — run DFT Surrogate analysis to populate this section.</p>`}
${section(6, 'Computational Details')}
<table><tbody>
  ${row('Generation method', 'MatterGen generative diffusion model')}
  ${row('Scoring metric', 'Absolute structural quality composite')}
  ${row('Score weights', 'Min. interatomic distance 40%, packing fraction 35%, density 25%')}
  ${row('Property estimation', 'Derived from lattice geometry and atomic radii (geometric estimates)')}
</tbody></table>
<div class="footer">Computational report generated ${generated} · Properties are computational estimates; experimental validation required.</div>
<div class="footer" style="margin-top:6pt;border:none;padding-top:0">${POWERED_FOOTER}</div>
</body></html>`;
}

// ---------------------------------------------------------------------------
// Patent Print
// ---------------------------------------------------------------------------

function buildPatentPrintHTML(data, st, sy, bo, co, ec, crystalImg) {
  const filed = new Date(data.generated_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  // Flag any appendices that were not generated so the exported document never
  // looks like a complete filing when sections are missing.
  const _missing = [];
  if (!sy) _missing.push('Crystallographic / symmetry data (Appendix B)');
  if (!bo && !(co && co.length)) _missing.push('Bonding & coordination data (Appendix C)');
  if (!ec) _missing.push('Economic indicators (Appendix D)');
  const incompleteBanner = _missing.length
    ? `<div style="border:1pt solid #b91c1c;background:#fff5f5;color:#7f1d1d;padding:8pt 12pt;font-size:9.5pt;margin-bottom:16pt;line-height:1.5"><b>⚠ INCOMPLETE DISCLOSURE.</b> The following sections were not generated and are omitted: ${_missing.join('; ')}. Run all structural analyses and the Economic Report before relying on this document for a patent filing.</div>`
    : '';
  const appRow = (label, val) => val != null && val !== '' ? `<tr><td class="lbl">${label}</td><td>${val}</td></tr>` : '';
  const claimsHTML = (data.claims || []).map((c, i) =>
    `<li style="${i === 0 ? 'font-weight:700' : ''}">${c.replace(/^\d+\.\s*/, '')}</li>`
  ).join('');
  const wyckoffTable = sy?.wyckoff_sites?.length ? `
    <table><thead><tr><th>Element</th><th>Wyckoff</th><th>Mult.</th><th>Fractional coordinates</th></tr></thead><tbody>
    ${sy.wyckoff_sites.map(w => `<tr><td><b>${w.element}</b></td><td>${w.wyckoff_letter}</td><td>${w.multiplicity}</td><td style="font-family:monospace">${(w.frac_coords||[]).map(c=>c.toFixed(4)).join(', ')}</td></tr>`).join('')}
    </tbody></table>` : '';
  const bondPairsTable = bo?.bond_pairs?.length ? `
    <table><thead><tr><th>Pair</th><th>Count</th><th>Min (Å)</th><th>Max (Å)</th><th>Mean (Å)</th></tr></thead><tbody>
    ${bo.bond_pairs.map(bp => `<tr><td><b>${bp.pair}</b></td><td>${bp.count}</td><td>${bp.min}</td><td>${bp.max}</td><td>${bp.mean}</td></tr>`).join('')}
    </tbody></table>` : '';
  const coordTable = co?.length ? `
    <table><thead><tr><th>#</th><th>Element</th><th>CN</th><th>Neighbor environment</th></tr></thead><tbody>
    ${co.map(c => `<tr><td>${c.index}</td><td><b>${c.element}</b></td><td>${c.coordination_number}</td><td>${Object.entries(c.neighbor_counts||{}).map(([el,n])=>`${el}×${n}`).join(', ')}</td></tr>`).join('')}
    </tbody></table>` : '';

  return `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>${data.title || 'Patent Disclosure'}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:Georgia,'Times New Roman',serif;font-size:11pt;color:#111;background:#fff;padding:0.75in 1in}
  h1{font-size:14pt;text-align:center;margin:0 0 4pt;line-height:1.4}
  .meta{font-size:9pt;text-align:center;color:#555;margin-bottom:24pt}
  .sec{font-size:10pt;font-weight:700;text-transform:uppercase;letter-spacing:.06em;margin:18pt 0 6pt;border-bottom:0.5pt solid #ccc;padding-bottom:3pt}
  .body{font-size:10.5pt;line-height:1.75;color:#222;margin-bottom:10pt}
  .abstract{border-left:2pt solid #444;padding:8pt 12pt;font-style:italic;font-size:10pt;color:#333;margin-bottom:16pt}
  ol.claims{padding-left:22pt}ol.claims li{margin-bottom:8pt;font-size:10.5pt;line-height:1.65}
  table{width:100%;border-collapse:collapse;margin:8pt 0;font-size:9pt}
  th{background:#f2f2f2;padding:4pt 8pt;text-align:left;border:0.5pt solid #bbb;font-weight:700}
  td{padding:4pt 8pt;border:0.5pt solid #bbb}td.lbl{color:#555;white-space:nowrap;width:30%}
  .fig{text-align:center;margin:14pt 0}
  .fig img{max-width:320pt;max-height:240pt;border:0.5pt solid #ddd}
  .fig-cap{font-size:9pt;font-style:italic;color:#555;margin-top:4pt}
  .footer{margin-top:28pt;padding-top:6pt;border-top:0.5pt solid #ccc;font-size:8pt;color:#888;text-align:center}
  @media print{body{padding:0}}
</style></head><body>
<h1>${data.title || `Novel Crystalline Compound: ${st.formula}`}</h1>
<div class="meta">PATENT DISCLOSURE &nbsp;·&nbsp; Status: PENDING &nbsp;·&nbsp; Date: ${filed} &nbsp;·&nbsp; Formula: ${st.formula}</div>
${incompleteBanner}
${crystalImg ? `<div class="fig"><img src="${crystalImg}" alt="3D crystal structure of ${st.formula}"><div class="fig-cap">Figure 1 — Three-dimensional crystal structure of ${st.formula}</div></div>` : ''}
<div class="sec">Abstract</div>
<div class="abstract">${data.abstract || ''}</div>
<div class="sec">1. Field of the Invention</div><div class="body">${data.field_of_invention || ''}</div>
<div class="sec">2. Background</div><div class="body">${data.background || ''}</div>
<div class="sec">3. Summary of the Invention</div><div class="body">${data.summary_of_invention || ''}</div>
<div class="sec">4. Detailed Description</div><div class="body">${data.detailed_description || ''}</div>
<div class="sec">5. Claims</div>
<ol class="claims">${claimsHTML}</ol>
<div class="sec">Appendix A — Structural Data</div>
<table><tbody>
${appRow('Empirical formula', `<b>${st.formula}</b>`)}
${appRow('Atom count', st.atom_count)}
${appRow('Density (est.)', st.density != null ? `${st.density} g cm⁻³` : null)}
${appRow('Packing fraction', st.packing_fraction != null ? `${(st.packing_fraction*100).toFixed(2)} %` : null)}
${appRow('Min. interatomic distance', st.min_interatomic_distance != null ? `${st.min_interatomic_distance} Å` : null)}
${appRow('Quality score', st.score != null ? `${(st.score*100).toFixed(1)}% (rank #${st.rank})` : null)}
</tbody></table>
${sy ? `<div class="sec">Appendix B — Crystallographic Data</div>
<table><tbody>
${appRow('Space group', `<b>${sy.space_group}</b> (No. ${sy.space_group_number})`)}
${appRow('Crystal system', sy.crystal_system)}${appRow('Point group', sy.point_group)}
${appRow('Hall symbol', sy.hall_symbol)}${appRow('Symmetry operations', sy.n_symmetry_ops)}
${sy.lattice ? appRow('Lattice (a, b, c)', `${sy.lattice.a}, ${sy.lattice.b}, ${sy.lattice.c} Å`) : ''}
${sy.lattice ? appRow('Angles (α, β, γ)', `${sy.lattice.alpha}°, ${sy.lattice.beta}°, ${sy.lattice.gamma}°`) : ''}
${sy.lattice ? appRow('Cell volume', `${sy.lattice.volume} Å³`) : ''}
</tbody></table>${wyckoffTable}` : ''}
${bo || co ? `<div class="sec">Appendix C — Bonding &amp; Coordination</div>
${bo?.stats ? `<table><tbody>${appRow('Total bonds', bo.stats.count)}${appRow('Bond length range', `${bo.stats.min} – ${bo.stats.max} Å`)}${appRow('Mean bond length', `${bo.stats.mean} Å (σ = ${bo.stats.std} Å)`)}</tbody></table>` : ''}
${bondPairsTable}${coordTable}` : ''}
${ec ? `<div class="sec">Appendix D — Economic Indicators</div>
<table><tbody>
${appRow('Estimated BOM cost', `${ec.cost_low} – ${ec.cost_high} / kg`)}
${appRow('Energy intensity', `${ec.energy_label} (${ec.energy_range})`)}
${appRow('Cost volatility', ec.volatility_level + (ec.volatility_dominant ? ` — ${ec.volatility_dominant} dominant` : ''))}
${appRow('Recyclability', `${ec.recyclability_level} (score ${ec.recyclability_score})`)}
${appRow('Supply concentration', `${ec.supply_concentration_index} — ${ec.supply_concentration_level} risk`)}
</tbody></table>${ec.data_sources?.length ? `<p style="font-size:8pt;color:#888;margin-top:6pt">Sources: ${ec.data_sources.join(' · ')}</p>` : ''}` : ''}
<div class="footer">Patent disclosure generated ${new Date(data.generated_at).toUTCString()} &nbsp;·&nbsp; Computationally derived — not a filed patent application. Requires legal review before submission.</div>
<div class="footer" style="margin-top:6pt;border:none;padding-top:0">${POWERED_FOOTER}</div>
</body></html>`;
}

module.exports = { POWERED_FOOTER, buildEconomicReportHTML, buildScientificDocHTML, buildPatentPrintHTML };
